import { useEffect, useState, useMemo } from 'react';
import { Typography, Grid } from '@mui/material';
import { useQueryClient } from 'react-query';
import { isDate } from 'validator';

// Our components
import Alert from 'components/Alert';
import { PrimaryButton } from 'components/Button/Button';
import CurrencyTextInput from 'components/Input/CurrencyTextInput';
import Dropdown from 'components/Dropdown/Dropdown';
import GetFullAddressPartialForm from 'components/Forms/addressRelated/GetFullAddressPartialForm';
import TextInput from 'components/Input/TextInput';
import Loader from 'components/Loader';
import LoanInquirySuccessModal from 'components/Modal/LoanInquiryConfirmationModal';
import OutstandingBalanceInput from 'components/Client/Onboarding/OutstandingBalanceInput';
import { NUMBER } from 'components/Input/Types';
import RateInput from 'components/Input/RateInput';
import StandardDatePicker from 'components/DatePicker/StandardDatePicker';
import SoraTextField from 'components/Input/SoraTextField';
import TenureDropdown from 'components/Client/Onboarding/TenureDropdown';
import RequiredFieldsTooltip from 'components/Forms/NewLoanForms/RequiredFieldsTooltip';

// Our Query Keys
import { ERROR_MESSAGE_DATA } from 'shared/query-keys';

// Our hooks 🪝
import useMutateCreateDeal from 'hooks/hubspot/useMutateCreateDeal';
import useMutateSaveNewLoanInquiry from 'hooks/newLoanInquiries/useMutateSaveNewLoanInquiry';
import useMutateUpdateLoanInquiry from 'hooks/newLoanInquiries/useMutateUpdatedLoanInquiry';
import useMutateSendGenericEmail from 'hooks/emails/useMutateSendGenericEmail';
import usePrepopulateState from 'hooks/prepopulate/usePrepopulateState';

// Utils
import { clearFormValues, isSubmissionReady } from 'shared/utils';
import createDefaultCloseDate from 'shared/utils/hubspot/createDefaultCloseDate';
import formatCollection from 'shared/utils/formatting/currency/formatCollection';

// Our Emails
import lendingEmailTemplate from 'emails/lendingEmailTemplate';

// Constants
import {
	EMPLOYMENT_STATUS_OPTIONS,
	PERSONAL,
	REFINANCE_OFFER_TYPE
} from 'shared/constants';

function PersonalLoanRefinanceForm({
	advisorInfo,
	clientInfo,
	clientProfileData,
	isClientNameProvided,
	financialData,
	loanRequestId,
	loanType
}) {
	const queryClient = useQueryClient();
	const currentURL = window.location.href;
	const atLoanRequestDetailsUrl = currentURL.includes('loan-request-details');

	// API Calls
	const saveLoanInquiry = useMutateSaveNewLoanInquiry();
	const { isLoading: savingLoanInquiry } = saveLoanInquiry;

	// Mutations
	const updateLoanInquiry = useMutateUpdateLoanInquiry();
	const sendGenericEmail = useMutateSendGenericEmail();
	const createDeal = useMutateCreateDeal();

	// Advisor Info
	const { advisorName, advisorEmail, advisorCompany, advisorWebsite } =
		advisorInfo;

	// Client Info
	const {
		annualIncome,
		assetsAmount,
		clientFullName,
		creditScoreValue,
		clientId,
		emailAddressValue,
		firstName,
		lastName
	} = clientInfo;

	const {
		currentOutstandingBalance,
		currentLoanInterestRateValue,
		currentLoanRemainingTermValue,
		currentLoanMonthlyPayment
	} = financialData;

	// Pre-populated state items
	const [startDate, setStartDate] = useState('');
	const [employmentStatus, setEmploymentStatus] = useState('Yes');

	// Existing loan info
	const [loanAmount, setLoanAmount] = useState('0');
	const [interestRate, setInterestRate] = useState('0');
	const [term, setTerm] = useState('');
	const [monthlyPayment, setMonthlyPayment] = useState('');
	const [rentOrOwn, setRentOrOwn] = useState('');
	const [monthlyHousingPayment, setMonthlyHousingPayment] = useState('');

	// Address related fields
	const [addressLine1, setAddressLine1] = useState('');
	const [addressLine2, setAddressLine2] = useState('');
	const [city, setCity] = useState('');
	const [state, setState] = useState('');
	const [zipCode, setZipCode] = useState('');

	const [additionalNotes, setAdditionalNotes] = useState('');
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	const formData = [loanAmount, interestRate, term];

	const loanRequestDetailsRequiredFields = [
		loanAmount,
		interestRate,
		term,
		rentOrOwn,
		monthlyHousingPayment,
		employmentStatus,
		startDate,
		addressLine1,
		city,
		state,
		zipCode
	];

	const isFormReady = useMemo(
		() =>
			isSubmissionReady(
				atLoanRequestDetailsUrl
					? loanRequestDetailsRequiredFields
					: formData
			) && isClientNameProvided,
		atLoanRequestDetailsUrl
			? loanRequestDetailsRequiredFields
			: [...formData, isClientNameProvided]
	);

	const isValidDate = useMemo(() => {
		const isStringDate = typeof startDate === 'string';
		// @ts-ignore
		const isDateType = startDate instanceof Date;
		if (isStringDate && startDate !== '') {
			const isDateCheckWithConversion = isDate(new Date(startDate));
			return isDateCheckWithConversion;
		}

		if (isDateType) {
			return isDate(startDate);
		}
		// if dob is null this returns false;
		return false;
	}, [startDate]);

	usePrepopulateState(
		[
			{
				setState: setLoanAmount,
				value: currentOutstandingBalance
			},
			{
				setState: setInterestRate,
				value: currentLoanInterestRateValue
			},
			{
				setState: setTerm,
				value: currentLoanRemainingTermValue
			},
			{
				setState: setMonthlyPayment,
				value: currentLoanMonthlyPayment
			},
			{
				setState: setAddressLine1,
				value: clientProfileData?.address?.addressLine1
			},
			{
				setState: setAddressLine2,
				value: clientProfileData?.address?.addressLine2
			},
			{
				setState: setCity,
				value: clientProfileData?.address?.city
			},
			{
				setState,
				value: clientProfileData?.address?.state
			},
			{
				setState: setZipCode,
				value: clientProfileData?.address?.zipCode
			}
		],
		[
			currentOutstandingBalance,
			currentLoanInterestRateValue,
			currentLoanRemainingTermValue,
			clientProfileData
		]
	);

	const sendFormData = () => {
		const sendToArray = ['lending@sorafinance.com'];
		const subject = `Personal Loan Refinance Request - ${firstName} ${lastName}`;

		const emailTemplate = lendingEmailTemplate({
			advisorInfo,
			clientInfo,
			financialData,
			clientFullName,
			loanAmount,
			loanRelatedDetails: [
				`Is Client Employed? ${employmentStatus}`,
				`Client's Employment Start Date: ${startDate}`,
				`Client's Monthly Housing Payment: ${monthlyHousingPayment}`,
				`Client's residence status: ${rentOrOwn}`,
				`Client's Address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}`,
				`Monthly Payment of current loan: ${monthlyPayment}`,
				`Outstanding Balance of Current Loan: ${loanAmount}`,
				`Interest Rate of Current Loan: ${interestRate}`,
				`Years Remaining of Current Loan: ${term}`
			],
			additionalNotes,
			lendingTypeRequested: 'Personal Loan Refinance'
		});

		sendGenericEmail.mutate(
			{
				firstName,
				lastName,
				subject,
				message: emailTemplate,
				sendToArray
			},
			{
				onSuccess: () => {
					setIsSuccessModalOpen(true);
					clearFormValues([
						setStartDate,
						setEmploymentStatus,
						setLoanAmount,
						setMonthlyPayment,
						setMonthlyHousingPayment,
						setRentOrOwn,
						setInterestRate,
						setTerm,
						setAddressLine1,
						setAddressLine2,
						setState,
						setCity,
						setZipCode,
						setAdditionalNotes
					]);
				}
			}
		);
	};

	const submitNewLoanInquiry = () => {
		const newLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			city,
			clientId,
			creditScore: creditScoreValue,
			interestRate,
			loanAmount,
			remainingTerm: term,
			dob: startDate,
			emailAddress: emailAddressValue,
			employmentStatus,
			firstName,
			lastName,
			monthlyHousingPayment,
			offerType: REFINANCE_OFFER_TYPE,
			rentOrOwn,
			startDate,
			state,
			totalAnnualIncome: annualIncome,
			totalAssetsValue: assetsAmount,
			tradelineType: PERSONAL,
			zipCode
		};

		// Call mutation to update user's data
		saveLoanInquiry.mutate(newLoanInquiryData, {
			onSuccess: () => {
				setIsSuccessModalOpen(true);
			},
			onError: () => {
				// Set error message
				queryClient.setQueryData(
					ERROR_MESSAGE_DATA,
					'There was an error making your loan request.'
				);
			}
		});
	};

	const updateLoanRequest = () => {
		const updatedLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			city,
			clientId,
			creditScore: creditScoreValue,
			interestRate,
			monthlyPayment,
			loanAmount,
			remainingTerm: term,
			dob: startDate,
			emailAddress: emailAddressValue,
			employmentStatus,
			firstName,
			lastName,
			monthlyHousingPayment,
			offerType: REFINANCE_OFFER_TYPE,
			rentOrOwn,
			startDate,
			state,
			totalAnnualIncome: annualIncome,
			totalAssetsValue: assetsAmount,
			tradelineType: PERSONAL,
			zipCode
		};

		// Call mutation to update user's data
		updateLoanInquiry.mutate(
			{ loanRequestId, updatedLoanInquiryData },
			{
				onSuccess: () => {
					clearFormValues([
						setStartDate,
						setEmploymentStatus,
						setLoanAmount,
						setMonthlyPayment,
						setMonthlyHousingPayment,
						setRentOrOwn,
						setInterestRate,
						setTerm,
						setAddressLine1,
						setAddressLine2,
						setState,
						setCity,
						setZipCode,
						setAdditionalNotes
					]);
				},
				onError: () => {
					// Set error message
					queryClient.setQueryData(
						ERROR_MESSAGE_DATA,
						'There was an error making your loan request.'
					);
				}
			}
		);
	};

	const { isLoading } = sendFormData;

	const submitForm = (event) => {
		event.preventDefault();
		const closeDate = createDefaultCloseDate();
		const valuesToFormat = [
			{
				type: 'currency',
				value: annualIncome,
				label: 'Annual Income:'
			},
			{
				type: 'currency',
				value: assetsAmount,
				label: 'Total assets value:'
			},
			{
				type: 'currency',
				value: loanAmount,
				label: 'Outstanding loan balance:'
			},
			{
				type: 'currency',
				value: monthlyPayment,
				label: 'Monthly payment of current loan:'
			},
			{
				type: 'rate',
				value: interestRate,
				label: 'Current annual interest rate:'
			},
			{
				type: 'currency',
				value: monthlyHousingPayment,
				label: 'Monthly rent or mortgage payment:'
			}
		];

		const formattedCollection = formatCollection(valuesToFormat);
		const advisorNotes = `Advisor's name: ${advisorName}
				Advisor's email: ${advisorEmail}
				Advisor's company: ${advisorCompany}
				Credit Score: ${creditScoreValue}
				${formattedCollection}
				Remaining term: ${term}
				Primary residence status: ${rentOrOwn}
				Employment status: ${employmentStatus}
				Employment start date: ${startDate}
				Address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}
				------------------------------------------------------------------------------------------------
				Advisor's notes: ${additionalNotes}`;

		createDeal.mutate({
			advisorInfo: {
				advisorName,
				email: advisorEmail,
				companyName: advisorCompany,
				companyDomain: advisorWebsite
			},
			properties: {
				amount: loanAmount,
				dealname: `${advisorName} / ${clientFullName}`,
				dealtype: loanType,
				borrower: clientFullName,
				closedate: closeDate
			},
			noteInfo: {
				hs_note_body: advisorNotes
			}
		});
		// If we're at /loan-inquiry, perform the following
		if (!atLoanRequestDetailsUrl) {
			submitNewLoanInquiry();
			sendFormData();
		}
		// Else if we're at /loan-inquiry-details, perform the following
		else if (atLoanRequestDetailsUrl) {
			// Call new mutations here patching the new loan inquiry
			updateLoanRequest();
		}
	};

	// Handlers for mutation
	const { isError: sendFormDataError } = sendFormData;

	return (
		<>
			{!atLoanRequestDetailsUrl && (
				<Grid item xs={12}>
					<Typography variant="h2Gascogne">
						Personal Loan Refinance
					</Typography>

					<Typography variant="body2">
						Fill in the information below to submit a loan request
						to Sora. You will be able to complete and review your
						loan request in the dashboard task bar.
					</Typography>
				</Grid>
			)}

			{isLoading || (savingLoanInquiry && <Loader />)}
			{!isLoading && !savingLoanInquiry && (
				<>
					{sendFormDataError && <Alert variant="error" />}

					{/* Modal shown on successful submission of  */}
					<LoanInquirySuccessModal isOpen={isSuccessModalOpen} />

					<Grid item xs={12} md={6}>
						{/* Outstanding Balance */}
						<OutstandingBalanceInput
							label="Outstanding loan balance *"
							outstandingBalance={loanAmount}
							setOutstandingBalance={setLoanAmount}
							inputProps={{
								'data-test': 'outstandingBalance'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Currently loan monthly payment */}
						<TextInput
							label="Monthly payment of current loan"
							value={monthlyPayment}
							onChange={setMonthlyPayment}
							type={NUMBER}
							inputProps={{
								'data-test': 'currentMonthlyPayment'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Current annual interest rate */}
						<RateInput
							type={NUMBER}
							rate={interestRate}
							setRate={setInterestRate}
							label="Current annual interest rate *"
							inputProps={{
								'data-test': 'currentInterestRate'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<TenureDropdown
							label="Remaining term *"
							subLabel="Time left in months"
							tenure={term}
							setTenure={setTerm}
							dropDownSX={{ width: '100%' }}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Rent or own */}
						<Dropdown
							items={[
								'Renter',
								'Homeowner',
								'Live with a family member',
								'Other'
							]}
							selected={rentOrOwn}
							onChange={setRentOrOwn}
							variant="outlined"
							label={
								atLoanRequestDetailsUrl
									? 'Primary residence status *'
									: 'Primary residence status'
							}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Monthly Rent or Mortgage Payment Amount */}
						<CurrencyTextInput
							label={
								atLoanRequestDetailsUrl
									? 'Monthly rent or mortgage payment *'
									: 'Monthly rent or mortgage payment'
							}
							value={monthlyHousingPayment}
							onChange={setMonthlyHousingPayment}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Employment Status */}
						<Dropdown
							items={EMPLOYMENT_STATUS_OPTIONS}
							selected={employmentStatus}
							onChange={setEmploymentStatus}
							variant="outlined"
							label={
								atLoanRequestDetailsUrl
									? 'Employment status *'
									: 'Employment status'
							}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Start date */}
						<StandardDatePicker
							id="startDate"
							label={
								atLoanRequestDetailsUrl
									? 'Employment start date *'
									: 'Employment start date'
							}
							onChange={setStartDate}
							value={startDate}
							error={startDate === null || !isValidDate}
							errorText="Start date needs to be a valid date"
							inputProps={{
								'data-test': 'startDate'
							}}
						/>
					</Grid>

					<GetFullAddressPartialForm
						label={
							atLoanRequestDetailsUrl ? 'Address *' : 'Address'
						}
						addressLine1={addressLine1}
						addressLine2={addressLine2}
						city={city}
						state={state}
						zipCode={zipCode}
						setAddressLine1={setAddressLine1}
						setAddressLine2={setAddressLine2}
						setCity={setCity}
						setState={setState}
						setZipCode={setZipCode}
					/>

					<Grid item xs={12}>
						<SoraTextField
							label="Additional Notes"
							value={additionalNotes}
							onChange={setAdditionalNotes}
						/>
					</Grid>
				</>
			)}
			<Grid item xs={12}>
				{!isFormReady && (
					<RequiredFieldsTooltip>
						<span>
							<PrimaryButton
								disabled={!isFormReady}
								onClick={submitForm}
							>
								Submit
							</PrimaryButton>
						</span>
					</RequiredFieldsTooltip>
				)}
				{isFormReady && (
					<PrimaryButton onClick={submitForm}>Submit</PrimaryButton>
				)}
			</Grid>
		</>
	);
}

export default PersonalLoanRefinanceForm;
